"use client";

/**
 * NK standalone auth (Fase 2.3 / ADR-VID-005) — substitui o JWT do TIDE APP.
 *
 * Não há login/register/change-password: um único token NK (compartilhado
 * pela equipe) é colado uma vez em /credencial e guardado em localStorage.
 * ProtectedRoute só checa PRESENÇA do token (o backend é quem valida o Bearer
 * a cada request; 401 limpa o token e redireciona — ver lib/api.ts).
 *
 * Estado do token via useSyncExternalStore (não setState-em-effect):
 * snapshot do servidor = sem token; `loading` = pré-hidratação, evita o
 * ProtectedRoute redirecionar antes do primeiro snapshot real do cliente.
 */

import { useRouter } from "next/navigation";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useSyncExternalStore,
} from "react";

import { clearNkToken, getNkToken } from "@/lib/auth-token";

const TOKEN_CHANGE_EVENT = "nk-token-change";

function subscribeToken(cb: () => void) {
  window.addEventListener("storage", cb);
  window.addEventListener(TOKEN_CHANGE_EVENT, cb);
  return () => {
    window.removeEventListener("storage", cb);
    window.removeEventListener(TOKEN_CHANGE_EVENT, cb);
  };
}

function emitTokenChange() {
  window.dispatchEvent(new Event(TOKEN_CHANGE_EVENT));
}

const emptySubscribe = () => () => {};

interface AuthContextType {
  hasToken: boolean;
  loading: boolean;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const hasToken = useSyncExternalStore(
    subscribeToken,
    () => !!getNkToken(),
    () => false,
  );
  const hydrated = useSyncExternalStore(
    emptySubscribe,
    () => true,
    () => false,
  );
  const router = useRouter();

  const logout = useCallback(() => {
    clearNkToken();
    emitTokenChange();
    router.push("/credencial");
  }, [router]);

  return (
    <AuthContext.Provider value={{ hasToken, loading: !hydrated, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

/** Envolve rotas que exigem o token NK presente. Sem token → redireciona /credencial. */
export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { hasToken, loading } = useAuth();
  const router = useRouter();

  useEffect(() => {
    if (!loading && !hasToken) {
      router.push("/credencial");
    }
  }, [hasToken, loading, router]);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-tide-bg-primary">
        <span className="text-tide-text-muted">Carregando…</span>
      </div>
    );
  }

  if (!hasToken) return null;

  return <>{children}</>;
}
